import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalAnimatedModal } from "@/components/ui/external-animated-modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Copy, Check, QrCode, Clock, CreditCard, Sparkles } from "lucide-react";

interface PixPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pixData: {
    qrCodeUrl?: string;
    copyPasteCode?: string;
    orderId: string;
    orderNumber: string;
    total: number;
  };
}

export function PixPaymentModal({
  open,
  onOpenChange,
  pixData
}: PixPaymentModalProps) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Remover simulação de confirmação automática - aguardar webhook real
  // O modal permanece aberto até que o webhook confirme o pagamento
  useEffect(() => {
    console.log('PIX Payment Modal opened, waiting for webhook confirmation');
  }, [open]);

  const handleCopyPix = async () => {
    if (!pixData.copyPasteCode) return;

    try {
      await navigator.clipboard.writeText(pixData.copyPasteCode);
      setCopied(true);
      toast({
        title: "Código PIX copiado!",
        description: "Cole no seu app bancário para realizar o pagamento.",
      });
      
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o código. Tente selecionar e copiar manualmente.",
        variant: "destructive"
      });
    }
  };

  // Converte base64 em data URL se necessário
  const qrCodeSrc = pixData.qrCodeUrl?.startsWith('data:') 
    ? pixData.qrCodeUrl 
    : pixData.qrCodeUrl 
      ? `data:image/png;base64,${pixData.qrCodeUrl}` 
      : undefined;

  return (
    <ExternalAnimatedModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      className="max-w-2xl w-full mx-auto dark-theme"
    >
      {/* Header com gradient golden */}
      <div className="relative mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400/20 to-orange-500/20 border border-yellow-400/30">
            <QrCode className="h-6 w-6 text-yellow-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold gradient-text bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              Pagamento PIX
            </h2>
            <p className="text-white/70 text-sm">
              Escaneie o QR Code ou copie o código para pagar
            </p>
          </div>
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="h-5 w-5 text-yellow-400/60 animate-pulse" />
        </div>
      </div>

      <div className="space-y-6">
        {/* Informações do Pedido */}
        <div className="dark-card glass-effect p-6 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-5 w-5 text-yellow-400" />
            <h3 className="font-semibold text-white">Detalhes do Pedido</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Pedido:</span>
              <span className="font-medium text-white bg-white/10 px-3 py-1 rounded-full text-sm">
                {pixData.orderNumber}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Valor:</span>
              <span className="font-bold text-2xl gradient-text bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                R$ {pixData.total.toFixed(2).replace('.', ',')}
              </span>
            </div>
          </div>
        </div>

        {/* QR Code */}
        {qrCodeSrc && (
          <div className="dark-card glass-effect p-6 rounded-xl border border-white/10">
            <div className="flex flex-col items-center space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="h-5 w-5 text-yellow-400" />
                <h3 className="font-semibold text-white">Escaneie o QR Code</h3>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-xl blur-xl"></div>
                <div className="relative bg-white p-6 rounded-xl shadow-xl border-2 border-yellow-400/30">
                  <img 
                    src={qrCodeSrc} 
                    alt="QR Code PIX" 
                    className="w-56 h-56 object-contain mx-auto"
                  />
                </div>
              </div>
              
              <p className="text-white/60 text-sm text-center max-w-sm">
                Abra o app do seu banco e escaneie o código acima para realizar o pagamento
              </p>
            </div>
          </div>
        )}

        {/* Código Copia e Cola */}
        {pixData.copyPasteCode && (
          <div className="dark-card glass-effect p-6 rounded-xl border border-white/10 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Copy className="h-5 w-5 text-yellow-400" />
              <h3 className="font-semibold text-white">Ou copie o código PIX</h3>
            </div>
            
            <div className="space-y-4">
              <div className="bg-black/30 p-4 rounded-lg border border-white/10">
                <Input
                  value={pixData.copyPasteCode}
                  readOnly
                  className="text-xs font-mono bg-transparent border-none focus:ring-0 text-center text-white/90 select-all"
                />
              </div>
              
              <Button 
                onClick={handleCopyPix}
                className={`w-full h-12 font-semibold transition-all duration-300 ${
                  copied 
                    ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg shadow-green-500/25" 
                    : "bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black shadow-lg shadow-yellow-400/25 hover:shadow-yellow-400/40"
                }`}
                size="lg"
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Código Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-5 w-5" />
                    Copiar Código PIX
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Instruções */}
        <div className="dark-card glass-effect p-6 rounded-xl border border-yellow-400/30 bg-gradient-to-r from-yellow-400/5 to-orange-500/5">
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-yellow-400/20 border border-yellow-400/30 flex-shrink-0 mt-1">
              <Clock className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-white flex items-center gap-2">
                Instruções para pagamento
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              </h3>
              <ul className="text-white/70 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                  O PIX será processado automaticamente
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                  Você receberá confirmação em tempo real
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                  Pagamento válido por 24 horas
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                  Esta tela será atualizada quando o pagamento for confirmado
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex gap-4 pt-6 border-t border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-12 bg-transparent border-white/20 text-white hover:bg-white/10 hover:border-white/30 transition-all duration-300"
            size="lg"
          >
            Fechar
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate(`/acompanhar-pedido?pedido=${pixData.orderId}`);
            }}
            className="flex-1 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold shadow-lg shadow-yellow-400/25 hover:shadow-yellow-400/40 transition-all duration-300"
            size="lg"
          >
            Acompanhar Pedido
          </Button>
        </div>
      </div>
    </ExternalAnimatedModal>
  );
}